//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import { UniversalPostType } from '@apis/universal-posts/universalPostsApiModel';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import { ExistsWithNetworkItem, IMediaMetadata } from '@models/INetworkItem';
import {
  TikTokPhotoFormState,
  TikTokVideoFormState,
} from '@universal-scheduling/models/SchedulerPostDetailsModels';
import { isDefined } from '@utils/filterPredicates';

export const DEFAULT_TIKTOK_VIDEO_POST_DETAILS_STATE: TikTokVideoFormState = {
  postType: UniversalPostType.TikTokVideoAutomaticPost,
  postNow: false,
};

export const DEFAULT_TIKTOK_PHOTO_POST_DETAILS_STATE: TikTokPhotoFormState = {
  postType: UniversalPostType.TikTokPhotoAutomaticPost,
  postNow: false,
};

export const MAX_TIKTOK_VIDEO_CAPTION_LENGTH = 2200;
export const MAX_TIKTOK_HASHTAG_COUNT = 5;
export const TIKTOK_MIN_DIMENSION_PX = 360;
export const TIKTOK_MIN_VIDEO_DURATION_MILLIS = 3000; // 3 seconds
export const TIKTOK_MAX_VIDEO_DURATION_MILLIS = 600000; // 10 minutes

export const MAX_TIKTOK_PHOTO_COUNT = 35;
export const MAX_TIKTOK_TITLE_LENGTH = 90;
export const MAX_TIKTOK_PHOTO_CAPTION_LENGTH = 4000;
export const TIKTOK_MAX_IMAGE_HEIGHT = 1920;
export const TIKTOK_MAX_IMAGE_WIDTH = 1080;
export const TIKTOK_MAX_IMAGE_FILE_SIZE_BYTES = 20 * 1024 * 1024; // 20 MB

export const TIKTOK_SUPPORTED_IMAGE_FILE_TYPES = [
  'image/jpeg',
  'image/jpg', // TikTok docs specify jpg https://business-api.tiktok.com/portal/docs?id=1803630424390658
  'image/webp',
];

export const TikTokPostDetailsFormFieldErrorText = {
  NoMediaOrText: 'Please add a caption to this post.',
  TextTooLongPhoto: `Please enter ${MAX_TIKTOK_PHOTO_CAPTION_LENGTH} characters or less.`,
  TextTooLongVideo: `Please enter ${MAX_TIKTOK_VIDEO_CAPTION_LENGTH} characters or less.`,
  TitleTooLong: `Please enter ${MAX_TIKTOK_TITLE_LENGTH} characters or less.`,
  TooManyHashtags: `Please include ${MAX_TIKTOK_HASHTAG_COUNT} hashtags or less.`,
} as const;

export const TikTokPostDetailsFormTooltips = {
  AIGenerated: `For authenticity and transparency, this label should be added to videos and/or audio that is generated or modified by AI, in full or in part.`,
  AutoAddMusic: `If checked, recommended music will be automatically added to this post. You can change the music directly in TikTok.`,
} as const;

export const isTikTokImageTooLarge = (
  mediaMetadata: IMediaMetadata | undefined,
) => {
  if (isDefined(mediaMetadata)) {
    const { height, width } = mediaMetadata;

    // The maximum resolution of the image is 1080 x 1920 pixels or 1920 x 1080 pixels. https://business-api.tiktok.com/portal/docs?id=1803630424390658
    return (
      Math.min(height, width) > TIKTOK_MAX_IMAGE_WIDTH ||
      Math.max(height, width) > TIKTOK_MAX_IMAGE_HEIGHT
    );
  }

  return false;
};

export const hasTikTokImagesToResize = (
  mediaItems: ExistsWithNetworkItem<IMlsMediaItem>[],
) => {
  return mediaItems.some(
    (item) =>
      isDefined(item.networkItem.mediaMetadata) &&
      isTikTokImageTooLarge(item.networkItem.mediaMetadata),
  );
};

export const isTikTokImageTooSmall = (
  mediaMetadata: IMediaMetadata | undefined,
) => {
  if (isDefined(mediaMetadata)) {
    const { height, width } = mediaMetadata;

    return height < TIKTOK_MIN_DIMENSION_PX || width < TIKTOK_MIN_DIMENSION_PX;
  }

  return false;
};

export const isTikTokImageFileValid = (
  mediaMetadata: IMediaMetadata | undefined,
) => {
  return isDefined(mediaMetadata?.contentType)
    ? TIKTOK_SUPPORTED_IMAGE_FILE_TYPES.includes(
        mediaMetadata.contentType.toLowerCase(),
      )
    : false;
};
